import { Center, Loader } from '@mantine/core';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AdminRoute, AppLayout, ProtectedRoute } from '@/components';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';

// Only the two pages every user hits on first load (signed-out ->
// LoginPage, signed-in -> HomePage) are bundled eagerly. Everything else
// (profile/history/change-password and the whole admin section, which most
// of the ~50 daily users never touch) is code-split via `lazy()` so the
// initial bundle doesn't force every user to download the admin UI before
// the routes list can even render.
const ChangePasswordPage = lazy(() =>
  import('@/pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const HistoryPage = lazy(() =>
  import('@/pages/HistoryPage').then((m) => ({ default: m.HistoryPage })),
);
const AdminRoutesPage = lazy(() =>
  import('@/pages/admin/AdminRoutesPage').then((m) => ({ default: m.AdminRoutesPage })),
);
const AdminDailyOverviewPage = lazy(() =>
  import('@/pages/admin/AdminDailyOverviewPage').then((m) => ({
    default: m.AdminDailyOverviewPage,
  })),
);
const AdminMonthlyBreakdownPage = lazy(() =>
  import('@/pages/admin/AdminMonthlyBreakdownPage').then((m) => ({
    default: m.AdminMonthlyBreakdownPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminUserDetailPage = lazy(() =>
  import('@/pages/admin/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
);
const AdminAuditLogPage = lazy(() =>
  import('@/pages/admin/AdminAuditLogPage').then((m) => ({ default: m.AdminAuditLogPage })),
);

/** Fallback shown while a lazy-loaded route chunk is downloading. */
const RouteFallback = () => (
  <Center mih="100vh">
    <Loader />
  </Center>
);

export const App = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<LoginPage />} path="/login" />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChangePasswordPage />
              </AppLayout>
            </ProtectedRoute>
          }
          path="/account/change-password"
        />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout>
                <HomePage />
              </AppLayout>
            </ProtectedRoute>
          }
          path="/"
        />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
          path="/profile"
        />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout>
                <HistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
          path="/profile/history"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminRoutesPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminDailyOverviewPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin/daily"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminMonthlyBreakdownPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin/monthly"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminUsersPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin/users"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminUserDetailPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin/users/:userId"
        />
        <Route
          element={
            <AdminRoute>
              <AppLayout>
                <AdminAuditLogPage />
              </AppLayout>
            </AdminRoute>
          }
          path="/admin/audit-log"
        />
      </Routes>
    </Suspense>
  );
};
